import { db } from "configs";
import { SpeciesEnum, UserRoleEnum } from "requests/generated";
import {
  GetPageVariables,
  StoredAnestheticProtocol,
  StoredAuthorization,
  StoredBreed,
  StoredDisclaimerProcedure,
  StoredMedication,
  StoredProcedure,
  StoredUser
} from "../../types";

export const saveProcedures = (procedures: StoredProcedure[]) =>
  db.procedures.clear().then(() => db.procedures.bulkPut(procedures));

export const saveUsers = (users: StoredUser[]) =>
  db.users.clear().then(() => db.users.bulkPut(users));

export const saveBreeds = (breeds: StoredBreed[]) =>
  db.breeds.clear().then(() => db.breeds.bulkPut(breeds));

export const saveProtocols = (protocols: StoredAnestheticProtocol[]) => {
  db.protocols.clear().then(() => db.protocols.bulkPut(protocols));
};

export const saveMedications = (medications: StoredMedication[]) =>
  db.medications.clear().then(() => db.medications.bulkPut(medications));

export const saveDisclaimerProcedures = (
  disclaimerProcedures: StoredDisclaimerProcedure[]
) =>
  db.disclaimerProcedures
    .clear()
    .then(() => db.disclaimerProcedures.bulkPut(disclaimerProcedures));

export const saveAuthorizations = (authorizations: StoredAuthorization[]) =>
  db.authorizations
    .clear()
    .then(() => db.authorizations.bulkPut(authorizations));

export const saveDisclaimerText = (disclaimerText: string) =>
  db.disclaimerText
    .clear()
    .then(() => db.disclaimerText.put({ key: "text", value: disclaimerText }));

export const loadUsers = ({
  page,
  perPage,
  ids,
  role,
  search
}: GetPageVariables<number> & { role?: UserRoleEnum[] }) => {
  if (ids) {
    return db.users
      .where("_id")
      .anyOf(ids)
      .toArray()
      .then((collection: StoredUser[]) => ({
        collection,
        totalCount: collection.length
      }));
  }

  const userFilter = (user: StoredUser) => {
    const fnLn = `${user.firstName.toLowerCase()}${user.lastName.toLowerCase()}`;
    const lnFn = `${user.lastName.toLowerCase()}${user.firstName.toLowerCase()}`;
    const srch = (search || "").toLowerCase();

    const isSearchValid = fnLn.includes(srch) || lnFn.includes(srch);
    const isRoleValid = role ? role.includes(user.userRole) : true;
    return isSearchValid && isRoleValid;
  };

  const usersPage = db.users
    .orderBy("firstName")
    .filter(userFilter)
    .offset((page - 1) * perPage)
    .limit(perPage)
    .toArray();

  const count = db.users
    .orderBy("firstName")
    .filter(userFilter)
    .count();

  return Promise.all([usersPage, count]).then(
    ([collection, totalCount]: [StoredUser[], number]) => ({
      collection,
      totalCount
    })
  );
};

export const loadProcedures = ({
  page,
  perPage,
  ids,
  search
}: GetPageVariables<string>) => {
  if (ids) {
    return db.procedures
      .where("name")
      .anyOf(ids)
      .toArray()
      .then((collection: StoredProcedure[]) => ({
        collection,
        totalCount: collection.length
      }));
  }

  const proceduresFilter = (procedure: StoredProcedure) =>
    procedure.name.toLowerCase().includes((search || "").toLowerCase());

  const proceduresPage = db.procedures
    .orderBy("name")
    .filter(proceduresFilter)
    .offset((page - 1) * perPage)
    .limit(perPage)
    .toArray();

  const count = db.procedures
    .orderBy("name")
    .filter(proceduresFilter)
    .count();

  return Promise.all([proceduresPage, count]).then(
    ([collection, totalCount]: [StoredProcedure[], number]) => ({
      collection,
      totalCount
    })
  );
};

export const loadBreeds = ({
  page,
  perPage,
  ids,
  search,
  species
}: GetPageVariables<string> & { species?: SpeciesEnum | null }) => {
  if (ids) {
    return db.breeds
      .where("_id")
      .anyOf(ids)
      .toArray()
      .then((collection: StoredBreed[]) => ({
        collection,
        totalCount: collection.length
      }));
  }

  const breedFilter = (breed: StoredBreed) => {
    const validSpecies = species ? breed.species === species : true;
    const validSearch = breed.name
      .toLowerCase()
      .includes((search || "").toLowerCase());
    return validSearch && validSpecies;
  };

  const breedsPage = db.breeds
    .orderBy("name")
    .filter(breedFilter)
    .offset((page - 1) * perPage)
    .limit(perPage)
    .toArray();

  const count = db.breeds
    .orderBy("name")
    .filter(breedFilter)
    .count();

  return Promise.all([breedsPage, count]).then(
    ([collection, totalCount]: [StoredBreed[], number]) => ({
      collection,
      totalCount
    })
  );
};

export const loadMedications = ({
  page,
  perPage,
  ids,
  search
}: GetPageVariables<string>) => {
  if (ids) {
    return db.medications
      .where("_id")
      .anyOf(ids)
      .toArray()
      .then((collection: StoredMedication[]) => ({
        collection,
        totalCount: collection.length
      }));
  }

  const medicationFilter = (medication: StoredMedication) => {
    const nameParts = medication.name
      .replace(/[(;)®]/g, " ")
      .split(" ")
      .filter(Boolean);

    return nameParts.some(part =>
      part.toLowerCase().startsWith((search || "").toLowerCase())
    );
  };

  const medicationsPage = db.medications
    .orderBy("name")
    .filter(medicationFilter)
    .offset((page - 1) * perPage)
    .limit(perPage)
    .toArray();

  const count = db.medications
    .orderBy("name")
    .filter(medicationFilter)
    .count();

  return Promise.all([medicationsPage, count]).then(
    ([collection, totalCount]: [StoredMedication[], number]) => ({
      collection,
      totalCount
    })
  );
};

export const getProtocols = (search?: string, doctorIds?: number[]) => {
  return db.protocols
    .where("name")
    .startsWithIgnoreCase(search || "")
    .filter(protocol => {
      if (protocol.defaultProtocol) {
        return true;
      }
      if (doctorIds) {
        return doctorIds.includes(protocol.createdBy._id);
      }
      return true;
    })
    .sortBy("name", prtcls =>
      prtcls.sort((a, b) => (a.defaultProtocol && !b.defaultProtocol ? -1 : 1))
    );
};
